import { Box, Card, Chip, Typography } from '@mui/material';

import Avatar from '@/components/common/Avatar';
import type { EventResponse } from '@/types';

interface MobileMentionItemProps {
  event: EventResponse;
  dateDay: string;
  dateMonth: string;
  dateTime: string;
  options: string[];
  moreOptionsCount: number;
  formattedVolume: string;
  onTradeClick: (eventId: string) => void;
}

export const MobileMentionItem = ({
  event,
  dateDay,
  dateMonth,
  dateTime,
  options,
  moreOptionsCount,
  formattedVolume,
  onTradeClick,
}: MobileMentionItemProps) => {
  const handleClick = () => {
    onTradeClick(event.id);
  };

  const renderOption = (option: string, index: number) => (
    <Chip
      key={index}
      label={option}
      sx={{
        bgcolor: 'background.default',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '6px',
        fontSize: '0.75rem',
        fontWeight: 500,
        height: 28,
        color: 'text.secondary',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    />
  );

  const renderedOptions = options.map(renderOption);

  return (
    <Card
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.5,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: 'none',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? '0 4px 12px rgba(0,0,0,0.4)'
              : '0 2px 8px rgba(0,0,0,0.1)',
        },
      }}
    >
      {/* Date Box - Left Side */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minWidth: 48,
          flexShrink: 0,
          pt: 0.5,
        }}
      >
        <Typography
          sx={{
            fontSize: '1.25rem',
            fontWeight: 700,
            color: 'text.primary',
            lineHeight: 1,
          }}
        >
          {dateDay}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            mt: 0.25,
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
          }}
        >
          {dateMonth}
        </Typography>
      </Box>

      {/* Event Content - Middle */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: 'text.primary',
            mb: 1,
            lineHeight: 1.4,
            wordBreak: 'break-word',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {event?.title || 'Untitled Event'}
        </Typography>

        {/* Time, Status, and Volume */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexWrap: 'wrap',
            mb: 1.5,
          }}
        >
          {dateTime && (
            <Chip
              label={dateTime}
              size="small"
              sx={{
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: 500,
                height: 24,
                color: 'text.secondary',
              }}
            />
          )}
          {/* Status logic can be added here if needed */}
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            {formattedVolume} Vol.
          </Typography>
        </Box>

        {/* Prediction Options */}
        <Box
          sx={{
            display: 'flex',
            gap: 0.75,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {renderedOptions}
          {moreOptionsCount > 0 && (
            <Box
              sx={{
                px: 1,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'text.secondary',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '6px',
                bgcolor: 'background.default',
              }}
            >
              +{moreOptionsCount}
            </Box>
          )}
        </Box>
      </Box>

      {/* Avatar - Right Side */}
      <Avatar
        src={event?.icon || event?.image}
        alt={event?.title || 'Event'}
        size={64}
        sx={{
          borderRadius: 1.5,
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider',
          fontSize: '1.5rem',
          fontWeight: 600,
          color: 'text.primary',
        }}
      />
    </Card>
  );
};
